/**
 * TB Supporter — NGO dashboard.
 * NON-FINANCIAL: awareness programmes, community outreach, volunteer network.
 * "Create Awareness Campaign" posts a NON-financial campaign (no targetAmount).
 * In-page section nav; real data; honest empty states; verified-NGO pill from
 * /supporters/me status.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard, Building2, CalendarDays, Megaphone, HandHeart, MapPinned,
  Library, MessagesSquare, Activity, Users, Bell, Bot, User, ArrowRight,
  BookMarked, MapPin, BadgeCheck, Plus, Loader2, CheckCircle2, BookOpen,
} from 'lucide-react'
import { GovLayout, GovPageTitle, GovCard, GovButton } from '@/components/layout/GovLayout'
import { FloatingTbAssistant } from '@/components/portal/FloatingTbAssistant'
import { SupporterRoleNav, type SupporterNavSection } from '@/components/supporter/SupporterRoleNav'
import {
  SupporterStatCard, SupporterSectionCard, SupporterResourceCard,
  SupporterCampaignCard, SupporterEmptyState,
} from '@/components/supporter/SupporterCards'
import { SupporterProfilePanel } from '@/components/supporter/SupporterProfilePanel'
import { SupporterCollaborationForm } from '@/components/supporter/SupporterCollaborationForm'
import { useAuthStore } from '@/stores/authStore'
import notificationService, { type AppNotification } from '@/services/notificationService'
import {
  saveResource, unsaveResource, getSavedResources,
  type Resource, type SavedResourceItem,
} from '@/services/resourceService'
import campaignService, { type Campaign } from '@/services/campaignService'
import { getMySupporterProfile } from '@/services/supporterProfileService'
import { SUPPORTER_STATUS_LABELS } from '@/services/supporterService'
import {
  useSupporterActivity, fetchActiveCampaigns, fetchResources,
  shareLink, campaignShareUrl, resourceShareUrl,
} from './supporterData'
import {
  AWARENESS_CAMPAIGN_TYPES, MEDICAL_DISCLAIMER, IMPACT_DISCLAIMER,
  COLLABORATION_OPTIONS,
} from './supporterContent'

const SECTIONS: SupporterNavSection[] = [
  { key: 'overview', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'org', label: 'Organisation Profile', icon: Building2 },
  { key: 'programmes', label: 'Awareness Programmes', icon: CalendarDays },
  { key: 'campaigns', label: 'Campaigns', icon: Megaphone },
  { key: 'volunteer', label: 'Volunteer Network', icon: HandHeart },
  { key: 'outreach', label: 'Community Outreach', icon: MapPinned },
  { key: 'resources', label: 'TB Resources', icon: Library },
  { key: 'collaboration', label: 'Collaboration', icon: MessagesSquare },
  { key: 'impact', label: 'Impact', icon: Activity },
  { key: 'community', label: 'Community', icon: Users },
  { key: 'notifications', label: 'Notifications', icon: Bell },
  { key: 'assistant', label: 'TB Assistant', icon: Bot },
  { key: 'profile', label: 'My Profile', icon: User },
]

const CAMPAIGN_TYPE_OPTIONS = [
  { value: 'AWARENESS', label: 'Awareness' },
  { value: 'EDUCATION', label: 'Education' },
  { value: 'COMMUNITY_OUTREACH', label: 'Community outreach' },
]

const inputClass = 'w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]'

const NgoSupporterDashboard = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const activity = useSupporterActivity()

  const [active, setActive] = useState('overview')
  const [orgName, setOrgName] = useState<string | null>(null)
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [myCampaigns, setMyCampaigns] = useState<Campaign[]>([])
  const [resources, setResources] = useState<Resource[]>([])
  const [saved, setSaved] = useState<SavedResourceItem[]>([])
  const [savedIds, setSavedIds] = useState<Set<number>>(new Set())
  const [joinedIds, setJoinedIds] = useState<Set<number>>(new Set())
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [loadingList, setLoadingList] = useState(true)

  const displayName = orgName || user?.fullName || (user ? `${user.firstName} ${user.lastName}`.trim() : '') || t('supporter.hero.friend', 'friend')
  const verified = activity.registrationStatus === 'APPROVED' || activity.registrationStatus === 'ONBOARDED'

  const reloadCampaigns = useCallback(async () => {
    const [act, mine] = await Promise.allSettled([
      fetchActiveCampaigns(AWARENESS_CAMPAIGN_TYPES, 12),
      campaignService.getMyCampaigns({ size: 50 }),
    ])
    setCampaigns(act.status === 'fulfilled' ? act.value : [])
    setMyCampaigns(mine.status === 'fulfilled' ? (mine.value.content ?? []) : [])
  }, [])

  useEffect(() => {
    let active0 = true
    getMySupporterProfile().then((p) => { if (active0 && p.organizationName) setOrgName(p.organizationName) }).catch(() => {})
    setLoadingList(true)
    Promise.allSettled([
      fetchActiveCampaigns(AWARENESS_CAMPAIGN_TYPES, 12),
      campaignService.getMyCampaigns({ size: 50 }),
      fetchResources({ audience: 'NGO', size: 8 }),
      fetchSaved(),
    ]).then(([c, mine, r]) => {
      if (!active0) return
      setCampaigns(c.status === 'fulfilled' ? c.value : [])
      setMyCampaigns(mine.status === 'fulfilled' ? (mine.value.content ?? []) : [])
      setResources(r.status === 'fulfilled' ? r.value : [])
      setLoadingList(false)
    })
    return () => { active0 = false }
  }, [])

  const fetchSaved = useCallback(async () => {
    try {
      const page = await getSavedResources(0, 50)
      const items = page.content ?? []
      setSaved(items)
      setSavedIds(new Set(items.map((i) => i.id)))
    } catch { setSaved([]) }
  }, [])

  useEffect(() => {
    if (active !== 'notifications') return
    notificationService.getNotifications(0, 20).then((p) => setNotifications(p.content ?? [])).catch(() => setNotifications([]))
  }, [active])

  const toggleSave = useCallback(async (r: Resource) => {
    setSavedIds((prev) => { const next = new Set(prev); if (next.has(r.id)) next.delete(r.id); else next.add(r.id); return next })
    try {
      if (savedIds.has(r.id)) await unsaveResource(r.id)
      else await saveResource(r.id)
      await fetchSaved()
    } catch { await fetchSaved() }
  }, [savedIds, fetchSaved])

  const joinCampaign = useCallback(async (c: Campaign) => {
    setJoinedIds((prev) => new Set(prev).add(c.id))
    try { await campaignService.participate(c.id) } catch { setJoinedIds((prev) => { const n = new Set(prev); n.delete(c.id); return n }) }
  }, [])

  const onShareCampaign = useCallback((c: Campaign) => { shareLink(campaignShareUrl(c), c.title) }, [])
  const onShareResource = useCallback((r: Resource) => { shareLink(resourceShareUrl(r), r.title) }, [])

  // Programmes split by status (from the org's own campaigns).
  const programmes = useMemo(() => ({
    upcoming: myCampaigns.filter((c) => c.status === 'ACTIVE' || c.status === 'DRAFT'),
    active: myCampaigns.filter((c) => c.status === 'ACTIVE'),
    completed: myCampaigns.filter((c) => c.status === 'COMPLETED' || c.status === 'ENDED'),
  }), [myCampaigns])

  const impactTiles = useMemo(() => [
    { icon: Megaphone, value: myCampaigns.length, labelKey: 'supporter.ngo.impact.programmes', label: 'Awareness Programmes', accent: 'bg-emerald-50 text-emerald-600' },
    { icon: Megaphone, value: activity.campaignsJoined, labelKey: 'supporter.ngo.impact.campaignsJoined', label: 'Campaigns Joined', accent: 'bg-blue-50 text-blue-600' },
    { icon: HandHeart, value: 0, labelKey: 'supporter.ngo.impact.volunteers', label: 'Volunteers Coordinated', dataRequired: true, accent: 'bg-violet-50 text-violet-600' },
    { icon: MapPinned, value: 0, labelKey: 'supporter.ngo.impact.outreach', label: 'Outreach Activities', dataRequired: true, accent: 'bg-cyan-50 text-cyan-600' },
    { icon: BookMarked, value: activity.resourcesSaved, labelKey: 'supporter.ngo.impact.resourcesShared', label: 'Resources Saved', accent: 'bg-amber-50 text-amber-600' },
    { icon: Users, value: 0, labelKey: 'supporter.ngo.impact.communitiesReached', label: 'Communities Reached', dataRequired: true, accent: 'bg-rose-50 text-rose-600' },
  ], [activity, myCampaigns.length])

  const primaryActions = [
    { label: t('supporter.ngo.actionProgramme', 'Create Awareness Programme'), icon: Plus, onClick: () => setActive('programmes') },
    { label: t('supporter.ngo.actionCampaign', 'Join a Campaign'), icon: Megaphone, onClick: () => setActive('campaigns') },
    { label: t('supporter.ngo.actionVolunteer', 'Coordinate Volunteers'), icon: HandHeart, onClick: () => setActive('volunteer') },
    { label: t('supporter.ngo.actionOutreach', 'Community Outreach'), icon: MapPinned, onClick: () => setActive('outreach') },
  ]

  return (
    <GovLayout>
      <FloatingTbAssistant />
      <div className="space-y-5">
        <GovPageTitle
          title={t('supporter.ngo.title', 'Welcome, {{name}}', { name: displayName })}
          subtitle={t('supporter.ngo.subtitle', 'Lead awareness programmes and community outreach for a TB-free India.')}
          icon={Building2}
        />

        <GovCard>
          <div className="flex flex-wrap items-center gap-2.5">
            {verified && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">
                <BadgeCheck className="w-4 h-4" /> {t('supporter.ngo.verified', 'Verified NGO')}
              </span>
            )}
            {!verified && activity.registrationStatus && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                {t('supporter.ngo.status', 'Status')}: {SUPPORTER_STATUS_LABELS[activity.registrationStatus as keyof typeof SUPPORTER_STATUS_LABELS] ?? activity.registrationStatus}
              </span>
            )}
            <GovButton variant="primary" size="sm" onClick={() => navigate('/assistant')} className="inline-flex items-center gap-1.5"><Bot className="w-4 h-4" /> {t('supporter.hero.tbAssistant', 'TB Assistant')}</GovButton>
            <GovButton variant="secondary" size="sm" onClick={() => setActive('notifications')} className="inline-flex items-center gap-1.5"><Bell className="w-4 h-4" /> {t('supporter.hero.notifications', 'Notifications')}
              {activity.unreadCount > 0 && <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full bg-red-500 text-white">{activity.unreadCount}</span>}
            </GovButton>
            <GovButton variant="secondary" size="sm" onClick={() => navigate(`/profile/${user?.username}`)} className="inline-flex items-center gap-1.5"><User className="w-4 h-4" /> {t('supporter.hero.profile', 'Profile')}</GovButton>
            <span className="text-xs text-gray-400 ml-auto">{t('supporter.ngo.nonFinancial', 'Awareness & outreach only — no payments or donations.')}</span>
          </div>
        </GovCard>

        <SupporterRoleNav sections={SECTIONS} active={active} onChange={setActive} />

        {/* Overview */}
        {active === 'overview' && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">{impactTiles.slice(0, 3).map((s) => <SupporterStatCard key={s.labelKey} {...s} />)}</div>
            <SupporterSectionCard title={t('supporter.ngo.primaryActions', 'What your organisation can do today')} icon={LayoutDashboard}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {primaryActions.map((a) => {
                  const Icon = a.icon
                  return (
                    <button key={a.label} onClick={a.onClick} className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 hover:bg-emerald-100 transition-colors text-left min-h-[56px]">
                      <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-emerald-600" /></div>
                      <span className="font-medium text-gray-900 text-sm flex-1 min-w-0">{a.label}</span>
                      <ArrowRight className="w-4 h-4 text-emerald-500 shrink-0" />
                    </button>
                  )
                })}
              </div>
            </SupporterSectionCard>
            <div className="grid lg:grid-cols-2 gap-4">
              <SupporterSectionCard title={t('supporter.ngo.myProgrammes', 'My awareness programmes')} icon={CalendarDays} action={<GovButton variant="ghost" size="sm" onClick={() => setActive('programmes')}>{t('supporter.actions.viewAll', 'View all')}</GovButton>}>
                {loadingList ? <SkeletonRows /> : myCampaigns.length === 0
                  ? <SupporterEmptyState icon="📅" title={t('supporter.ngo.emptyProgrammes', 'No programmes yet')} description={t('supporter.ngo.emptyProgrammesDesc', 'Create your first awareness programme.')} actionLabel={t('supporter.ngo.actionProgramme', 'Create Awareness Programme')} onAction={() => setActive('programmes')} />
                  : <div className="space-y-3">{myCampaigns.slice(0, 3).map((c) => <SupporterCampaignCard key={c.id} campaign={c} joined onShare={onShareCampaign} onLearnMore={(cm) => navigate(`/campaigns/${cm.id}`)} />)}</div>}
              </SupporterSectionCard>
              <SupporterSectionCard title={t('supporter.ngo.activeCampaigns', 'Active campaigns to join')} icon={Megaphone} action={<GovButton variant="ghost" size="sm" onClick={() => setActive('campaigns')}>{t('supporter.actions.viewAll', 'View all')}</GovButton>}>
                {loadingList ? <SkeletonRows /> : campaigns.length === 0
                  ? <SupporterEmptyState icon="📣" title={t('supporter.ngo.emptyCampaigns', 'No active campaigns right now')} actionLabel={t('supporter.ngo.actionCampaign', 'Explore Campaigns')} onAction={() => setActive('campaigns')} />
                  : <div className="space-y-3">{campaigns.slice(0, 3).map((c) => <SupporterCampaignCard key={c.id} campaign={c} joined={joinedIds.has(c.id)} onJoin={joinCampaign} onShare={onShareCampaign} onLearnMore={(cm) => navigate(`/campaigns/${cm.id}`)} />)}</div>}
              </SupporterSectionCard>
            </div>
          </div>
        )}

        {/* Organisation Profile */}
        {active === 'org' && (
          <SupporterSectionCard title={t('supporter.ngo.orgTitle', 'Organisation profile')} description={t('supporter.ngo.orgDesc', 'Organisation details help us recommend relevant outreach content.')} icon={Building2}>
            <div className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 mb-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0"><Building2 className="w-6 h-6 text-emerald-600" /></div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-gray-900 truncate">{displayName}</p>
                  {verified && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-medium"><BadgeCheck className="w-3 h-3" /> {t('supporter.ngo.verified', 'Verified NGO')}</span>}
                </div>
                <p className="text-xs text-gray-500">{t('supporter.ngo.orgTag', 'NGO TB Awareness Partner')}</p>
              </div>
            </div>
            <SupporterProfilePanel role="ngo" />
          </SupporterSectionCard>
        )}

        {/* Awareness Programmes */}
        {active === 'programmes' && (
          <div className="space-y-5">
            <CreateCampaignForm onCreated={() => { void reloadCampaigns() }} />
            <SupporterSectionCard title={t('supporter.ngo.programmesUpcoming', 'Upcoming / active programmes')} icon={CalendarDays}>
              {programmes.upcoming.length === 0
                ? <SupporterEmptyState icon="📅" title={t('supporter.ngo.emptyUpcoming', 'No upcoming programmes')} />
                : <div className="grid sm:grid-cols-2 gap-3">{programmes.upcoming.map((c) => <SupporterCampaignCard key={c.id} campaign={c} joined onShare={onShareCampaign} onLearnMore={(cm) => navigate(`/campaigns/${cm.id}`)} />)}</div>}
            </SupporterSectionCard>
            <SupporterSectionCard title={t('supporter.ngo.programmesCompleted', 'Completed programmes')} icon={CheckCircle2}>
              {programmes.completed.length === 0
                ? <SupporterEmptyState icon="✅" title={t('supporter.ngo.emptyCompleted', 'No completed programmes yet')} />
                : <div className="grid sm:grid-cols-2 gap-3">{programmes.completed.map((c) => <SupporterCampaignCard key={c.id} campaign={c} joined onShare={onShareCampaign} onLearnMore={(cm) => navigate(`/campaigns/${cm.id}`)} />)}</div>}
            </SupporterSectionCard>
          </div>
        )}

        {/* Campaigns */}
        {active === 'campaigns' && (
          <SupporterSectionCard title={t('supporter.ngo.campaignsTitle', 'Awareness campaigns')} description={t('supporter.ngo.campaignsDesc', 'Joining means awareness participation — no payment or sponsorship.')} icon={Megaphone}>
            {loadingList ? <SkeletonRows /> : campaigns.length === 0
              ? <SupporterEmptyState icon="📣" title={t('supporter.ngo.emptyCampaigns', 'No active campaigns right now')} actionLabel={t('supporter.ngo.actionCampaign', 'Explore Campaigns')} onAction={() => navigate('/campaigns')} />
              : <div className="grid sm:grid-cols-2 gap-3">{campaigns.map((c) => <SupporterCampaignCard key={c.id} campaign={c} joined={joinedIds.has(c.id)} onJoin={joinCampaign} onShare={onShareCampaign} onLearnMore={(cm) => navigate(`/campaigns/${cm.id}`)} />)}</div>}
          </SupporterSectionCard>
        )}

        {/* Volunteer Network */}
        {active === 'volunteer' && (
          <SupporterSectionCard title={t('supporter.ngo.volunteerTitle', 'Volunteer network')} description={t('supporter.ngo.volunteerDesc', 'Coordinate non-monetary volunteer awareness activities.')} icon={HandHeart}>
            {loadingList ? <SkeletonRows /> : campaigns.length === 0
              ? <SupporterEmptyState icon="🤝" title={t('supporter.ngo.emptyVolunteer', 'No volunteer opportunities right now')} />
              : <div className="grid sm:grid-cols-2 gap-3">{campaigns.slice(0, 6).map((c) => <SupporterCampaignCard key={c.id} campaign={c} joined={joinedIds.has(c.id)} onJoin={joinCampaign} onShare={onShareCampaign} onLearnMore={(cm) => navigate(`/campaigns/${cm.id}`)} />)}</div>}
            <div className="mt-4"><GovButton variant="secondary" size="sm" onClick={() => navigate('/volunteer')}>{t('supporter.ngo.exploreOpportunities', 'Explore opportunities')}<ArrowRight className="w-4 h-4 ml-1" /></GovButton></div>
          </SupporterSectionCard>
        )}

        {/* Community Outreach */}
        {active === 'outreach' && (
          <SupporterSectionCard title={t('supporter.ngo.outreachTitle', 'Community outreach')} description={t('supporter.ngo.outreachDesc', 'Awareness activities and resources for community fieldwork.')} icon={MapPinned}>
            <div className="grid sm:grid-cols-2 gap-3">
              <OutreachLink icon={MapPin} label={t('supporter.ngo.outreachCentres', 'Find TB centres')} desc={t('supporter.ngo.outreachCentresDesc', 'Locate official TB facilities.')} to="/find-tb-centre" />
              <OutreachLink icon={BookOpen} label={t('supporter.ngo.outreachKnowledge', 'Outreach knowledge')} desc={t('supporter.ngo.outreachKnowledgeDesc', 'Trusted TB awareness content.')} to="/knowledge" />
              <OutreachLink icon={Library} label={t('supporter.ngo.outreachResources', 'IEC resources')} desc={t('supporter.ngo.outreachResourcesDesc', 'Awareness materials for the field.')} to="/resources" />
              <OutreachLink icon={Megaphone} label={t('supporter.ngo.outreachCampaigns', 'Outreach campaigns')} desc={t('supporter.ngo.outreachCampaignsDesc', 'Join community campaigns.')} to="/campaigns" />
            </div>
          </SupporterSectionCard>
        )}

        {/* Resources */}
        {active === 'resources' && (
          <SupporterSectionCard title={t('supporter.ngo.resourcesTitle', 'TB resources')} description={t('supporter.ngo.resourcesDesc', 'Awareness and outreach materials for NGOs.')} icon={Library}>
            {loadingList ? <SkeletonRows /> : resources.length === 0
              ? <SupporterEmptyState icon="🗂️" title={t('supporter.ngo.emptyResources', 'Resources coming soon')} actionLabel={t('supporter.ngo.browseResources', 'Browse Resources')} onAction={() => navigate('/resources')} />
              : <div className="grid sm:grid-cols-2 gap-3">{resources.map((r) => <SupporterResourceCard key={r.id} resource={r} saved={savedIds.has(r.id)} onToggleSave={toggleSave} onShare={onShareResource} />)}</div>}
            <div className="mt-4"><GovButton variant="secondary" size="sm" onClick={() => navigate('/resources')}>{t('supporter.ngo.browseResources', 'Browse all resources')}<ArrowRight className="w-4 h-4 ml-1" /></GovButton></div>
          </SupporterSectionCard>
        )}

        {/* Collaboration */}
        {active === 'collaboration' && (
          <SupporterSectionCard title={t('supporter.collaboration.heading', 'Collaboration')} description={t('supporter.collaboration.ngoDesc', 'Express interest in government initiatives, community campaigns or volunteer networks. This is not a financial sponsorship.')} icon={MessagesSquare}>
            <SupporterCollaborationForm interestOptions={COLLABORATION_OPTIONS.ngo} />
          </SupporterSectionCard>
        )}

        {/* Impact */}
        {active === 'impact' && (
          <div className="space-y-5">
            <SupporterSectionCard title={t('supporter.ngo.impactTitle', 'Impact')} description={IMPACT_DISCLAIMER} icon={Activity}>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">{impactTiles.map((s) => <SupporterStatCard key={s.labelKey} {...s} />)}</div>
            </SupporterSectionCard>
            <SupporterSectionCard title={t('supporter.ngo.savedTitle', 'Saved resources')} icon={BookMarked}>
              {saved.length === 0
                ? <SupporterEmptyState icon="🔖" title={t('supporter.ngo.emptySaved', 'No saved resources yet')} actionLabel={t('supporter.ngo.browseResources', 'Browse Resources')} onAction={() => setActive('resources')} />
                : <div className="grid sm:grid-cols-2 gap-3">{saved.map((r) => <SupporterResourceCard key={r.id} resource={r} saved onToggleSave={toggleSave} onShare={onShareResource} />)}</div>}
            </SupporterSectionCard>
          </div>
        )}

        {/* Community */}
        {active === 'community' && (
          <SupporterSectionCard title={t('supporter.ngo.communityTitle', 'Community')} description={t('supporter.ngo.communityDesc', 'Join discussions, stories and awareness activities.')} icon={Users}>
            <div className="grid sm:grid-cols-2 gap-3">
              <CommunityLink icon={MessagesSquare} label={t('supporter.ngo.communityFeed', 'Community feed')} desc={t('supporter.ngo.communityFeedDesc', 'Discussions, posts and stories.')} to="/feed" />
              <CommunityLink icon={Megaphone} label={t('supporter.ngo.communityCampaigns', 'Awareness activities')} desc={t('supporter.ngo.communityCampaignsDesc', 'Join awareness campaigns.')} to="/campaigns" />
              <CommunityLink icon={BookOpen} label={t('supporter.ngo.communityKnowledge', 'Knowledge hub')} desc={t('supporter.ngo.communityKnowledgeDesc', 'Trusted TB articles.')} to="/knowledge" />
              <CommunityLink icon={Users} label={t('supporter.ngo.communityGroups', 'Groups')} desc={t('supporter.ngo.communityGroupsDesc', 'Connect with support groups.')} to="/groups" />
            </div>
          </SupporterSectionCard>
        )}

        {/* Notifications */}
        {active === 'notifications' && (
          <SupporterSectionCard title={t('supporter.ngo.notificationsTitle', 'Notifications')} icon={Bell} action={<GovButton variant="ghost" size="sm" onClick={() => notificationService.markAllAsRead().then(() => setNotifications((n) => n.map((x) => ({ ...x, isRead: true }))))}>{t('supporter.ngo.markAllRead', 'Mark all read')}</GovButton>}>
            {notifications.length === 0
              ? <SupporterEmptyState icon="🔔" title={t('supporter.ngo.emptyNotifications', 'You\'re all caught up')} description={t('supporter.ngo.emptyNotificationsDesc', 'No notifications right now.')} />
              : <div className="divide-y divide-gray-100">{notifications.map((n) => (
                  <div key={n.id} className={`py-3 ${n.isRead ? '' : 'bg-emerald-50/40 -mx-2 px-2 rounded-lg'}`}>
                    <p className="text-sm font-medium text-gray-900">{n.title}</p>
                    <p className="text-sm text-gray-600">{n.message}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{new Date(n.createdAt).toLocaleString('en-IN')}</p>
                  </div>
                ))}</div>}
          </SupporterSectionCard>
        )}

        {/* Assistant */}
        {active === 'assistant' && (
          <SupporterSectionCard title={t('supporter.ngo.assistantTitle', 'TB Assistant')} description={MEDICAL_DISCLAIMER} icon={Bot}>
            <p className="text-sm text-gray-600">{t('supporter.ngo.assistantDesc', 'Discover TB awareness information, official resources and TB centres. The assistant does not replace a doctor.')}</p>
            <div className="mt-4 flex flex-wrap gap-2.5">
              <GovButton variant="primary" size="sm" onClick={() => navigate('/assistant')} className="inline-flex items-center gap-1.5"><Bot className="w-4 h-4" /> {t('supporter.ngo.openAssistant', 'Open TB Assistant')}</GovButton>
              <GovButton variant="secondary" size="sm" onClick={() => navigate('/find-tb-centre')} className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {t('supporter.ngo.findCentre', 'Find a TB Centre')}</GovButton>
            </div>
          </SupporterSectionCard>
        )}

        {/* Profile */}
        {active === 'profile' && (
          <SupporterSectionCard title={t('supporter.ngo.profileTitle', 'My profile')} description={t('supporter.ngo.profileDesc', 'Manage your details and outreach interests.')} icon={User}>
            <SupporterProfilePanel role="ngo" />
          </SupporterSectionCard>
        )}
      </div>
    </GovLayout>
  )
}

// ── Non-financial "Create Awareness Campaign" inline form ──
function CreateCampaignForm({ onCreated }: { onCreated: () => void }) {
  const { t } = useTranslation()
  const [title, setTitle] = useState('')
  const [type, setType] = useState('AWARENESS')
  const [description, setDescription] = useState('')
  const [location, setLocation] = useState('')
  const [startDate, setStartDate] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    setSubmitting(true)
    setError(null)
    setSuccess(false)
    try {
      // NO targetAmount / collectedAmount — strictly non-financial.
      await campaignService.createCampaign({
        title: title.trim(),
        type,
        description: description.trim() || undefined,
        location: location.trim() || undefined,
        startDate: startDate || undefined,
        targetParticipants: undefined,
        targetAmount: undefined,
      })
      setSuccess(true)
      setTitle('')
      setDescription('')
      setLocation('')
      setStartDate('')
      onCreated()
    } catch (err: any) {
      setError(err?.response?.data?.message || t('supporter.ngo.createError', 'Could not create the programme. Please try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <SupporterSectionCard title={t('supporter.ngo.createProgramme', 'Create an awareness programme')} description={t('supporter.ngo.createProgrammeDesc', 'Non-financial — awareness/education/community outreach only. No amount or donation field.')} icon={Plus}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label htmlFor="np-title" className="block text-sm font-medium text-gray-700 mb-1">{t('supporter.ngo.programmeTitle', 'Programme title')} *</label>
            <input id="np-title" value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={255} className={inputClass} />
          </div>
          <div>
            <label htmlFor="np-type" className="block text-sm font-medium text-gray-700 mb-1">{t('supporter.ngo.programmeType', 'Type')}</label>
            <select id="np-type" value={type} onChange={(e) => setType(e.target.value)} className={inputClass}>
              {CAMPAIGN_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="np-location" className="block text-sm font-medium text-gray-700 mb-1">{t('supporter.ngo.programmeLocation', 'Location (optional)')}</label>
            <input id="np-location" value={location} onChange={(e) => setLocation(e.target.value)} maxLength={255} className={inputClass} />
          </div>
          <div>
            <label htmlFor="np-start" className="block text-sm font-medium text-gray-700 mb-1">{t('supporter.ngo.programmeStart', 'Start date (optional)')}</label>
            <input id="np-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputClass} />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="np-desc" className="block text-sm font-medium text-gray-700 mb-1">{t('supporter.ngo.programmeDesc', 'Description (optional)')}</label>
            <textarea id="np-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={2000} className={`${inputClass} resize-y`} />
          </div>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {success && (
          <p className="text-sm text-emerald-600 flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4" />
            {t('supporter.ngo.createSuccess', 'Programme created. It will appear under your programmes once published.')}
          </p>
        )}
        <GovButton variant="primary" type="submit" className="inline-flex items-center gap-2">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {t('supporter.ngo.createSubmit', 'Create programme')}
        </GovButton>
      </form>
    </SupporterSectionCard>
  )
}

function SkeletonRows() {
  return <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 rounded-xl bg-gray-100 animate-pulse" />)}</div>
}

function CommunityLink({ icon: Icon, label, desc, to }: { icon: React.ComponentType<{ className?: string }>; label: string; desc: string; to: string }) {
  return (
    <Link to={to} className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors min-h-[56px]">
      <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-emerald-600" /></div>
      <div className="min-w-0 flex-1"><p className="font-medium text-gray-900 text-sm truncate">{label}</p><p className="text-xs text-gray-500 truncate">{desc}</p></div>
      <ArrowRight className="w-4 h-4 text-gray-400 shrink-0" />
    </Link>
  )
}

function OutreachLink({ icon: Icon, label, desc, to }: { icon: React.ComponentType<{ className?: string }>; label: string; desc: string; to: string }) {
  return (
    <Link to={to} className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 hover:bg-emerald-50 transition-colors min-h-[56px]">
      <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-emerald-600" /></div>
      <div className="min-w-0 flex-1"><p className="font-medium text-gray-900 text-sm truncate">{label}</p><p className="text-xs text-gray-500 truncate">{desc}</p></div>
      <ArrowRight className="w-4 h-4 text-gray-400 shrink-0" />
    </Link>
  )
}

export default NgoSupporterDashboard